import croUsdMaxCsv from './cro-usd-max.csv';

/** UTC calendar day `YYYY-MM-DD` → CRO price in USD (one row per day in source file). */
let croUsdByUtcDay: Map<string, number> | null = null;
/** Ascending sorted keys from `croUsdByUtcDay` for nearest-day lookup. */
let sortedUtcDayKeys: string[] = [];

function parseCroUsdCsv(text: string): Map<string, number> {
  const map = new Map<string, number>();
  const lines = text.trim().split(/\r?\n/);
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const firstComma = line.indexOf(',');
    if (firstComma < 0) continue;
    const snapped = line.slice(0, firstComma).trim();
    const rest = line.slice(firstComma + 1);
    const secondComma = rest.indexOf(',');
    const priceStr = secondComma >= 0 ? rest.slice(0, secondComma) : rest;
    const price = parseFloat(priceStr.trim());
    if (!Number.isFinite(price) || price <= 0) continue;
    const dayKey = snapped.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) continue;
    map.set(dayKey, price);
  }
  return map;
}

export function ensureCroUsdDailyLoaded(): void {
  if (croUsdByUtcDay != null) return;
  croUsdByUtcDay = parseCroUsdCsv(croUsdMaxCsv);
  sortedUtcDayKeys = Array.from(croUsdByUtcDay.keys()).sort();
}

/** USD per 1 CRO at UTC calendar day of `date`, or `null` if outside bundled range. */
export function croUsdPerUnitOnUtcDay(date: Date): number | null {
  ensureCroUsdDailyLoaded();
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  const key = `${y}-${m}-${d}`;
  const v = croUsdByUtcDay!.get(key);
  return v != null ? v : null;
}

/**
 * Same as {@link croUsdPerUnitOnUtcDay} for an exact UTC day, otherwise the **latest** row with
 * `day ≤` that day (fills gaps in the CSV and aligns with “last known daily” usage).
 * Before first / after last row in the file, clamps to first / last price.
 */
export function croUsdPerUnitOnUtcDayNearest(date: Date): number | null {
  ensureCroUsdDailyLoaded();
  const map = croUsdByUtcDay!;
  const keys = sortedUtcDayKeys;
  if (keys.length === 0) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  const dayKey = `${y}-${m}-${d}`;
  const hit = map.get(dayKey);
  if (hit != null) return hit;
  if (dayKey < keys[0]) return map.get(keys[0]) ?? null;
  if (dayKey > keys[keys.length - 1]) return map.get(keys[keys.length - 1]) ?? null;
  let i = keys.length - 1;
  while (i >= 0 && keys[i] > dayKey) i--;
  return i >= 0 ? map.get(keys[i])! : map.get(keys[0])!;
}
